import {
  ArrowCircleDown,
  ArrowCircleUp,
  IndeterminateCheckBox,
  LocalHospital,
} from '@mui/icons-material'
import { IconButton } from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { Inventory } from '../../models/Inventory'
import { RootState, useAppDispatch, useAppSelector } from '../../store'
import { axiosPost } from './Axios'
import ProgressChart from './Chart/ProgressChart'
import RangeChart from './Chart/RangeChart'
import { ApiResult, SimpleScoreTitle } from '../../models/Enums'
import TextAreaDiv from './TextAreaDiv'
import { User } from '../../models/User'
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt'
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt'
import { setIn } from 'formik'
import { toast } from 'react-hot-toast'
import {
  updateInventoryLikeCnt,
  updateInventoryMemo,
  updateInventoryPoint,
} from '../../store/mainSlice'

type Prop = {
  inventory: Inventory
  inventories: Inventory[]
  payMin: number
  payMax: number
  pointMax: number
  likeMax: number
  recommendMax: number
  isLike: boolean
  setIsLike: (isLike: boolean) => void
  isShowMemo: boolean
  user: User
}

function InventoryListItem(props: Prop) {
  // inventory를 Local에 복사해놓고 UI를 Update하고. Inventory 변경시에는 setInventories로 원본을 변경한다
  const [inventory, setInventory] = useState<Inventory>(props.inventory)
  const me = useAppSelector((state: RootState) => state.user.me)
  const dispatch = useAppDispatch()

  const scoreIndex: number[] = [1, 5, 7, 9, 11, 13, 15]
  const backColor: string[] = [
    '#5959ef',
    '#5959ef',
    '#5959ef',
    '#f7ac2e',
    '#f7ac2e',
    '#f7ac2e',
    '#fb7218',
    '#fb7218',
    '#fb7218',
  ]

  useEffect(() => {
    setInventory(props.inventory)
  }, [props.inventory])

  const updateMemo = async (value: string) => {
    const payload = {
      userId: inventory.userId.toString(),
      inventoryId: inventory.id.toString(),
      memo: value,
    }

    const result = await axiosPost('/api/inventory/update_memo', payload)
    if (result !== 'success') {
      return
    }

    const newInventory = { ...props.inventory, memo: value }

    setInventory(newInventory)

    // [inventory change]
    dispatch(
      updateInventoryMemo({
        inventoryId: props.inventory.id,
        memo: value,
      })
    )
    // props.setInventories(
    //   props.inventories.map((inv) =>
    //     inv.id === newInventory.id ? newInventory : inv
    //   )
    // )
  }

  // 좋아요
  const handleLikePoint = async (isAdd: boolean) => {
    const payload = {
      likeUserId: me.id,
      jobUserId: inventory.userId,
      jobId: inventory.job.id,
      isAdd: isAdd ? 1 : 0,
    }

    try {
      const result = await axiosPost(
        '/api/inventory/update_inventory_like',
        payload
      )

      if (result.apiResult === ApiResult.SUCCESS) {
        const likeCnt = result.data as number

        props.setIsLike(isAdd)

        const newInventory = {
          ...props.inventory,
          likeCnt,
        }

        setInventory(newInventory)

        // [inventory change]
        dispatch(
          updateInventoryLikeCnt({
            inventoryId: props.inventory.id,
            likeCnt,
          })
        )
        // props.setInventories(
        //   props.inventories.map((inv) =>
        //     inv.id === newInventory.id ? newInventory : inv
        //   )
        // )
        toast.success(isAdd ? '좋아요를 보냈습니다' : '좋아요를 취소했습니다')
      }
    } catch (error) {
      console.log('getInventoryLike error', error)
    }
  }

  // 내점수
  const handleAddPoint = async (value: number) => {
    let newValue = inventory.point + value
    if (newValue < 0) {
      newValue = 0
    }

    const payload = {
      userId: inventory.userId,
      inventoryId: inventory.id,
      point: newValue,
    }

    const result = await axiosPost('/api/inventory/update_point', payload)
    if (result !== 'success') {
      return
    }

    const newInventory = { ...props.inventory, point: newValue }

    setInventory(newInventory)

    // [inventory change]
    dispatch(
      updateInventoryPoint({
        inventoryId: props.inventory.id,
        point: newValue,
      })
    )
    // props.setInventories(
    //   props.inventories.map((inv) =>
    //     inv.id === newInventory.id ? newInventory : inv
    //   )
    // )
  }

  return (
    <Container>
      <Link to={'/job/' + inventory.job.id}>
        <img src={inventory.job.photo} />
      </Link>
      <Information>
        <div>{inventory.job.name}</div>
        <Content>
          <Progresses>
            <div>만족도</div>
            <ProgressChart
              barColor="tomato"
              value={inventory.job.satisfaction}
              unit="%"
              align="center"
              minValue={0}
              maxValue={100}
              height={30}
              border={true}
            />
            <div>
              임금 : {inventory.job.payLower.toLocaleString()}만원 ~{' '}
              {inventory.job.payUpper.toLocaleString()}만원
            </div>
            <RangeChart
              minRange={parseInt(process.env.REACT_APP_PAY_MIN ?? '1000')}
              maxRange={parseInt(process.env.REACT_APP_PAY_MAX ?? '17000')}
              minValue={inventory.job.payLower}
              maxValue={inventory.job.payUpper}
              color="blue"
            />
            <Point>
              {me.id === inventory.userId ? '내점수 : ' : '본인점수 : '}
              {me.id === inventory.userId && props.isShowMemo ? ( // isShowMemo=true이면 Grid가 아닌 List형태이고 List 에서만 값을 변경할 수 있음
                <>
                  <IconButton onClick={() => handleAddPoint(1)}>
                    <ArrowCircleUp />
                  </IconButton>

                  <span>{inventory.point.toLocaleString()}</span>
                  <IconButton onClick={() => handleAddPoint(-1)}>
                    <ArrowCircleDown />
                  </IconButton>
                </>
              ) : (
                <span>{inventory.point.toLocaleString()}</span>
              )}
            </Point>
            <ProgressChart
              barColor="green"
              value={inventory.point}
              align="center"
              unit="점"
              minValue={0}
              maxValue={props.pointMax}
              height={10}
              border={true}
            />
            <Point>
              친구/멘토 추천 :{' '}
              <span>{inventory.recommendCnt.toLocaleString()}</span>
            </Point>
            <ProgressChart
              barColor="olive"
              value={inventory.recommendCnt}
              unit="점"
              align="center"
              minValue={0}
              maxValue={props.recommendMax}
              height={10}
              border={true}
            />
            <Point>
              좋아요 :{' '}
              {me.id !== inventory.userId ? ( // isShowMemo=true이면 Grid가 아닌 List형태이고 List 에서만 값을 변경할 수 있음
                <>
                  <IconButton
                    style={{ marginTop: '-8px' }}
                    onClick={() => handleLikePoint(!props.isLike)}
                  >
                    {props.isLike ? (
                      <ThumbUpAltIcon style={{ color: 'dodgerblue' }} />
                    ) : (
                      <ThumbUpOffAltIcon />
                    )}
                  </IconButton>

                  <span>{inventory.likeCnt.toLocaleString()}</span>
                </>
              ) : (
                <span>{inventory.likeCnt.toLocaleString()}</span>
              )}
            </Point>
            <ProgressChart
              barColor="green"
              value={inventory.likeCnt}
              unit="점"
              align="center"
              minValue={0}
              maxValue={props.likeMax}
              height={10}
              border={true}
            />
          </Progresses>
          <Score>
            <div>능력/지식/성격 요소</div>
            <div>
              {scoreIndex.map((i) => (
                <ScoreCode key={i}>
                  <div>{SimpleScoreTitle[i]}</div>
                  <div>{inventory.job.scores[i * 5].scoreCode.name}</div>
                </ScoreCode>
              ))}
            </div>
          </Score>
          {props.isShowMemo && (
            <Memo>
              <div>메모</div>
              <TextAreaDiv
                initialValue={inventory.memo}
                initialEditMode={false}
                setValue={(memo: string) => updateMemo(memo)}
              />
            </Memo>
          )}
        </Content>
      </Information>
    </Container>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: row;
  background: #f5f5f5;
  border: 1px solid #eee;
  img {
    width: 329px;
    height: 329px;

    @media (max-width: 768px) {
      width: 100%;
    }
  }

  @media (max-width: 768px) {
    flex-direction: column;
    max-height: auto;
    width: 100%;
  }
`

const Information = styled.div`
  display: flex;
  flex-direction: column;
  padding: 10px 30px 0;

  & > div:nth-child(1) {
    font-size: 1.2rem;
    font-weight: 600;
    padding-bottom: 10px;
  }

  @media (max-width: 768px) {
    padding: 0 0 40px;
    margin: 0 0 10px;
  }
`
const Content = styled.div`
  display: flex;
  flex-direction: row;
  @media (max-width: 768px) {
    flex-direction: column;
    row-gap: 20px;
  }
  column-gap: 20px;
`

const Progresses = styled.div`
  width: 350px;
  //height: 32px;
  font-size: 1rem;
  font-weight: normal;

  // 임금 위
  & > div:nth-child(3) {
    padding-top: 10px;
  }
  // 내점수 위
  & > div:nth-child(5) {
    margin-top: -10px;
  }
  // 내점수 막대 위
  & > div:nth-child(6) {
    margin-top: 0px;
  }
  // 친구 멘토 추천 위
  & > div:nth-child(7) {
    padding-top: 20px;
  }
  // 좋아요 위
  & > div:nth-child(9) {
    padding-top: 20px;
  }
  // 좋아요 막대 위
  & > div:nth-child(10) {
    margin-top: -7px;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`
const Point = styled.div`
  padding-top: 10px;
  display: flex;
  flex-direction: row;
  & > button {
    padding-top: 0;
  }
`
const Score = styled.div`
  width: 280px;
  padding-left: 30px;

  @media (max-width: 768px) {
    padding-left: 0;
    padding-top: 20px;
  }
`

const ScoreCode = styled.div`
  display: flex;
  flex-direction: row;
  padding-top: 10px;
  column-gap: 10px;

  div:nth-child(1) {
    width: 80px;
    text-align: center;
    background-color: #7474a0;
    color: white;
  }
  div:nth-child(2) {
  }
`

const Memo = styled.div`
  width: 300px;
  textarea {
    width: 100%;
    height: 120px;
    border: 1px solid #ddd;
    padding: 10px;
  }
  button {
    margin-top: 0;
  }
`

export default InventoryListItem
